<template>
    <DCard
        :title="__('settings.tax_code_mapping.title')"
        :padding="true"
    >
        <div class="tax-code-mapping">
            <!-- Existing Mappings -->
            <div
                v-for="(mapping, index) in mappings"
                :key="mapping.id || `new-${index}`"
                class="mapping-row"
            >
                <DRow class="align-items-center">
                    <DCol sm="4">
                        <DFormRow
                            :label="__('settings.tax_code_mapping.old_tax_code')"
                            :error="errors[`mappings.${index}.old_tax_code`]"
                            :hint="__('settings.tax_code_mapping.old_tax_code_hint')"
                        >
                            <DInput
                                v-model="mapping.old_tax_code"
                                is-required
                                :placeholder="__('settings.tax_code_mapping.old_tax_code_placeholder')"
                                :is-valid="isFieldValid(`mappings.${index}.old_tax_code`)"
                                :disabled="!mapping.isEditing"
                                maxlength="255"
                            />
                        </DFormRow>
                    </DCol>

                    <DCol sm="4">
                        <DFormRow
                            :label="__('settings.tax_code_mapping.new_tax_code')"
                            :error="errors[`mappings.${index}.new_tax_code`]"
                            :hint="__('settings.tax_code_mapping.new_tax_code_hint')"
                        >
                            <DInput
                                v-model="mapping.new_tax_code"
                                is-required
                                :placeholder="__('settings.tax_code_mapping.new_tax_code_placeholder')"
                                :is-valid="isFieldValid(`mappings.${index}.new_tax_code`)"
                                :disabled="mapping.id && !mapping.isEditing"
                                maxlength="255"
                            />
                        </DFormRow>
                    </DCol>

                    <DCol sm="3">
                        <DFormRow
                            :label="__('settings.tax_code_mapping.description')"
                            :error="errors[`mappings.${index}.description`]"
                            :hint="__('settings.tax_code_mapping.description_hint')"
                        >
                            <DInput
                                v-model="mapping.description"
                                :placeholder="__('settings.tax_code_mapping.description_placeholder')"
                                :is-valid="isFieldValid(`mappings.${index}.description`)"
                                :disabled="mapping.id && !mapping.isEditing"
                                maxlength="255"
                            />
                        </DFormRow>
                    </DCol>

                    <DCol
                        sm="1"
                        class="d-flex"
                        style="gap:10px"
                    >
                        <DButton
                            v-if="mapping.id && !mapping.isEditing"
                            mini
                            icon="pencil"
                            @click="toggleEdit(index)"
                        />

                        <DButton
                            v-if="mapping.id && mapping.isEditing"
                            mini
                            color="success"
                            icon="check"
                            variant="outline"
                            @click="toggleEdit(index)"
                        />

                        <DButton
                            color="danger"
                            mini
                            icon="trash-can-outline"
                            @click="removeMapping(index)"
                        />
                    </DCol>
                </DRow>
            </div>

            <!-- Add -->
            <DRow class="mt-3">
                <DCol>
                    <DButton
                        icon="plus"
                        :label="__('settings.tax_code_mapping.add_mapping')"
                        variant="outline"
                        @click="addMapping"
                    />
                </DCol>
            </DRow>

            <!-- Save -->
            <DRow class="mt-4">
                <DCol>
                    <DButton
                        color="success"
                        :label="__('settings.tax_code_mapping.save')"
                        :disabled="!canSave || isSaving"
                        @click="saveMappings"
                    />
                </DCol>
            </DRow>
        </div>
    </DCard>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'

const props = defineProps({
    initialMappings: {
        type: Array,
        default: () => []
    },
    errors: {
        type: Object,
        default: () => ({})
    }
})

const mappings = ref([])
const originalMappings = ref([])
const isSaving = ref(false)

function createEmptyMapping () {
    return {
        id: null,
        old_tax_code: '',
        new_tax_code: '',
        description: '',
        isEditing: true
    }
}

function normalizeMappings (items) {
    return items.map(item => ({
        old_tax_code: item.old_tax_code,
        new_tax_code: item.new_tax_code,
        description: item.description
    }))
}

function initializeMappings () {
    if (props.initialMappings.length > 0) {
        mappings.value = props.initialMappings.map(m => ({
            ...m,
            isEditing: false
        }))
    } else {
        mappings.value = [createEmptyMapping()]
    }

    originalMappings.value = JSON.stringify(
        normalizeMappings(mappings.value)
    )
}

initializeMappings()

watch(
    () => props.initialMappings,
    () => {
        if (Object.keys(props.errors).length > 0) {
            return
        }

        initializeMappings()
    }
)

const hasChanges = computed(() =>
    JSON.stringify(normalizeMappings(mappings.value)) !== originalMappings.value
)

const isAnyOldEditing = computed(() =>
    mappings.value.some(m => m.id && m.isEditing)
)

const canSave = computed(() => {
    if (isAnyOldEditing.value) {
        return false
    }

    if (!hasChanges.value) {
        return false
    }

    const originalCount = JSON.parse(originalMappings.value).length
    const currentCount = mappings.value.length

    if (currentCount < originalCount) {
        return true
    }

    return mappings.value.some(m =>
        m.old_tax_code.trim() && m.new_tax_code.trim()
    )
})

function addMapping () {
    mappings.value.push(createEmptyMapping())
}

function toggleEdit (index) {
    mappings.value[index].isEditing = !mappings.value[index].isEditing
}

function removeMapping (index) {
    mappings.value.splice(index, 1)
}

function isFieldValid (fieldPath) {
    return !props.errors[fieldPath]
}

function saveMappings () {
    isSaving.value = true

    const payload = normalizeMappings(mappings.value)

    router.post(
        route('settings.tax-code-mappings.store'),
        { mappings: payload },
        {
            preserveScroll: true,
            onFinish: () => {
                isSaving.value = false
            }
        }
    )
}
</script>

<style scoped lang="scss">
.tax-code-mapping {
    .mapping-row {
        margin-bottom: 1rem;

        &:not(:last-child) {
            padding-bottom: 1rem;
            border-bottom: 1px solid #e0e0e0;
        }
    }

}
</style>
